<template>
  <div class="text-start">
    <!--assessment title-->
    <v-row>
      <v-col class="pt-0">
        <span class="textLight--text font-weight-bold text-start text-h4 mb-1">
          {{ getLocalisedValue(assessment.title) }}
        </span>
      </v-col>
    </v-row>

    <!--assessment content QUIZ time out-->
    <v-row>
      <v-col class="py-0">
        <span class="text-h6 font-weight-medium primary--text">
          {{ $t("course.quiz") }}:
          {{ quizTimeOut }}
          {{ $t("course.min") }}
        </span>
      </v-col>
    </v-row>

    <!--assessment content QUIZ running-->
    <template v-if="isQuizRunning">
      <!--assessment quiz question card-->
      <v-row
        v-for="(quizQuestionAnswer, index) in quizQuestionAnswers"
        :key="'quizQuestionAnswer-' + index"
        align="start"
      >
        <v-col cols="12">
          <v-card flat class="rounded-2">
            <div class="d-flex pa-4 body-1 textLight--text text-start">
              <span class="pe-4"> {{ index + 1 }}. </span>

              <div class="flex-grow-1">
                <p class="ma-0">
                  {{ getLocalisedValue(quizQuestionAnswer.question) }}
                </p>

                <v-radio-group
                  v-model="quizQuestionAnswer.givenAnswerKey"
                  hide-details
                  class="pb-2"
                >
                  <v-radio
                    v-for="(
                      quizQuestionAnswerChoice, index
                    ) in quizQuestionAnswer.choices"
                    :key="'quizQuestionAnswerChoice-' + index"
                    :label="getLocalisedValue(quizQuestionAnswerChoice.value)"
                    :value="quizQuestionAnswerChoice.key"
                    :disabled="!isQuizChoiceSelectionEnabled"
                  />
                </v-radio-group>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!--assessment quiz mark complete button-->
      <v-row class="my-10">
        <v-col>
          <v-btn
            :disabled="isMarkAsCompleteDisabledForQuiz"
            depressed
            class="primary caption text-capitalize py-5 rounded-2"
            :loading="loading"
            @click="markAsComplete"
          >
            <v-icon size="14" class="me-1"> mdi-check </v-icon>

            {{ $t("course.markComplete") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <!--assessment description-->
      <v-row v-if="hasContents(assessment.description)" class="mb-6">
        <v-col>
          <span
            class="body-1 textLight--text text-start"
            v-html="getLocalisedValue(assessment.description)"
          />
        </v-col>
      </v-row>

      <!--assessment quiz timed warning card-->
      <v-row>
        <v-col cols="12" sm="10" md="11" lg="9">
          <v-card
            flat
            class="rounded-2"
            style="border-left: solid 12px rgba(89, 40, 229, 1)"
          >
            <div class="d-inline-flex pa-4">
              <v-icon size="42" color="rgba(28, 27, 31, 1)" class="pe-4">
                mdi-timer-outline
              </v-icon>

              <div class="text-start textLight--text">
                <p class="text-h6 font-weight-medium ma-0">
                  {{ $t("course.timedQuizTitle") }}
                </p>

                <p class="caption ma-0">
                  <span style="opacity: 0.7">
                    {{ $t("course.timedQuizBody1") }}
                  </span>

                  <span class="font-weight-bold">
                    {{ quizTimeOut }}
                    {{ $t("course.minutes").toLowerCase() }}
                  </span>

                  <span style="opacity: 0.7">
                    {{ $t("course.timedQuizBody2") }}
                  </span>
                </p>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!--assessment quiz start button-->
      <v-row>
        <v-col>
          <v-btn
            depressed
            class="
              primary
              caption
              text-capitalize
              py-5
              rounded-2
              text-uppercase
            "
            min-width="130"
            @click="startQuiz"
          >
            {{ $t("course.start") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="mb-10" />
    </template>
  </div>
</template>

<script>
import { addQuizAttempt } from "../methods/addQuizAttempt";
import { bulkDownloadQuizAttempts } from "@/app/quiz-module/helpers/quizPdfHelper";

export default {
  name: "AssessmentContent",
  props: {
    assessment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      isQuizRunning: false,
      isQuizChoiceSelectionEnabled: false,
      quizQuestionAnswers: [],

      subscriberInfo: null,
    };
  },
  computed: {
    quizTimeOut() {
      return this.assessment?.quiz?.timeOut;
    },
    isMarkAsCompleteDisabledForQuiz() {
      return this.quizQuestionAnswers.some(x => x.givenAnswerKey == null);
    },
  },
  apollo: {
    getSubscriberProfile() {
      return {
        query: require("../../user-module/api/query/getSubscriberProfile.gql"),
        result({ data }) {
          if (data?.getSubscriberProfile) {
            this.subscriberInfo = data.getSubscriberProfile;
          }
        },
      };
    },
  },
  methods: {
    startQuiz() {
      this.isQuizRunning = true;
      this.isQuizChoiceSelectionEnabled = true;
      const questionAnswers = this.assessment?.quiz?.questionAnswers;
      const questionsDisplayedCount =
        this.assessment?.quiz?.questionsDisplayedCount ?? 0;

      this.quizQuestionAnswers = this.getRandomSubArray(
        questionAnswers,
        questionsDisplayedCount
      )?.map(x => {
        return {
          ...x,
          questionId: x._id,
          givenAnswerKey: null,
        };
      });

      if (this.quizTimeOut) {
        let availableTimeMillis = this.quizTimeOut * 60000;

        this.$emit("onQuizTimerUpdate", {
          isQuizRunning: this.isQuizRunning,
          duration: this.$moment.utc(availableTimeMillis).format("mm:ss"),
        });

        const quizTimer = setInterval(() => {
          availableTimeMillis -= 1000;

          if (availableTimeMillis <= 0 || !this.isQuizRunning) {
            this.isQuizChoiceSelectionEnabled = false;
            clearInterval(quizTimer);
          }

          this.$emit("onQuizTimerUpdate", {
            isQuizRunning: this.isQuizRunning,
            duration: this.$moment.utc(availableTimeMillis).format("mm:ss"),
          });
        }, 1000);
      }
    },
    downloadAttemptPdf(quizAttempt) {
      bulkDownloadQuizAttempts({
        quizAttempts: [
          {
            ...quizAttempt,
            title: this.getLocalisedValue(this.assessment.title, {
              preferredLocale: "en",
            }),
          },
        ],
        subscriberInfo: this.subscriberInfo,
      });
    },
    markAsComplete() {
      if (!this.loading) {
        const questionAnswers = this.quizQuestionAnswers.map(x => {
          return {
            questionId: x._id,
            givenAnswerKey: x.givenAnswerKey,
          };
        });

        this.addQuizAttempt(questionAnswers);
      }
    },
    addQuizAttempt(questionAnswers) {
      if (!this.loading) {
        this.loading = true;

        const input = {
          id: this.assessment._id,
          questionAnswers,
        };

        addQuizAttempt(input).then(response => {
          if (response.data) {
            if (response.data.attempt) {
              // this.downloadAttemptPdf(response.data.attempt);
            }

            this.$router.replace({
              name: "AssessmentAttemptDetails",
              params: { id: response.data._id },
            });
          } else if (response.error) {
            this.$toast.error($t("somethingWentWrong"));
          }

          this.loading = false;
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped />
